import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import {
  NO_SIGN_IN_ADDRESSES,
  preferredAppTarget,
  type ChurchSignInAddresses,
  type SignInApp,
} from "@how/tenancy";
import type { AppEnv } from "./env";
import { startHandoff } from "./handoff";
import { useSupabase } from "./session";
import type { AppSupabaseClient } from "./supabase";

const answerSchema = z.object({
  dash: z.string().nullable(),
  members: z.string().nullable(),
  preferred: z.enum(["how", "church"]),
});

/**
 * The church's live sign-in addresses and "Our preferred address" (public.church_sign_in_addresses, P6), kept a
 * minute. An answer of another shape is a church with none: every link then uses HoW's address (ADR 0067 A6).
 */
export function churchSignInAddressesQuery(supabase: AppSupabaseClient, tenantId: string) {
  return queryOptions({
    queryKey: ["church-sign-in-addresses", tenantId],
    staleTime: 60_000,
    queryFn: async (): Promise<ChurchSignInAddresses> => {
      const { data, error } = await supabase.rpc("church_sign_in_addresses", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      const parsed = answerSchema.safeParse(data);
      return parsed.success ? parsed.data : NO_SIGN_IN_ADDRESSES;
    },
  });
}

/** The church's answer for the menus ("Open at …"), or a church with none until it comes, on error, or for null. */
export function useChurchSignInAddresses(tenantId: string | null): ChurchSignInAddresses {
  const supabase = useSupabase();
  const answer = useQuery({
    ...churchSignInAddressesQuery(supabase, tenantId ?? ""),
    enabled: tenantId !== null,
  });
  return answer.data ?? NO_SIGN_IN_ADDRESSES;
}

/** One of HoW's own links between the apps (R9): which church, which app, which page there. */
export interface ChurchAppLink {
  /** Null when the caller cannot name the church by id: HoW's address is used. */
  readonly tenantId: string | null;
  readonly slug: string;
  readonly app: SignInApp;
  readonly path: string;
}

/**
 * Opens a church's app through the hand-off at the address its preference names (R9): the church's own when it
 * prefers it and that app's address is live, otherwise HoW's. The preference and live state are read now, never from
 * the menus' minute-old copy (D8); an answer that cannot be read means HoW's address, which always works. Hosts come
 * only from the database (A5).
 */
export async function openChurchApp(
  supabase: AppSupabaseClient,
  queryClient: QueryClient,
  env: AppEnv,
  link: ChurchAppLink,
): Promise<void> {
  const addresses =
    link.tenantId === null
      ? NO_SIGN_IN_ADDRESSES
      : await queryClient
          .query({ ...churchSignInAddressesQuery(supabase, link.tenantId), staleTime: 0 })
          .catch((): ChurchSignInAddresses => NO_SIGN_IN_ADDRESSES);
  const target = preferredAppTarget(
    addresses,
    link.slug,
    link.app,
    env.VITE_HOW_PLATFORM_BASE_DOMAIN,
    link.path,
  );
  await startHandoff(supabase, target.host, target.path);
}

/** `useHandoff` for HoW's own links between the apps: the same shape, a church link instead of a host. */
export function useOpenChurchApp(env: AppEnv) {
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const m = useMutation({
    mutationFn: (link: ChurchAppLink) => openChurchApp(supabase, queryClient, env, link),
  });
  return {
    open: (link: ChurchAppLink) => {
      if (m.isPending) return;
      m.mutate(link);
    },
    isPending: m.isPending,
    error: m.error as unknown,
  };
}
